/**
 * Model-level maintenance depth blocks.
 *
 * The maintenance template was the most duplicated page type on the site
 * (mean 3-gram overlap ~0.57 between unrelated models) because every page
 * carried the same generic servicing prose around a nearly identical interval
 * table. These blocks are generated from the model's OWN rows — its powertrain
 * mix, cylinder count, transmission type, curb weight, year span and the years
 * its schedule actually changed — so divergence comes from fact selection, not
 * from swapping adjectives.
 *
 * Voice: blunt, compressed, number first, no filler.
 */

import { buildMaintenanceProfile, type MaintenanceContext } from "@/lib/maintenance";
import { buildMaintenanceYearRows } from "@/lib/maintenance-years";
import type { NarrativeSection } from "@/lib/spec-narrative";

const BASE_OIL = 7500;
const LABOUR_LOW = 95;
const LABOUR_HIGH = 175;

function n0(v: number): string {
  return Math.round(v).toLocaleString();
}

interface Row {
  year?: number | null;
  fuel_type?: string | null;
  atv_type?: string | null;
  cylinders?: string | number | null;
  displacement?: number | string | null;
  transmission?: string | null;
  drive?: string | null;
}

function num(v: unknown): number | null {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : null;
}

function share(rows: Row[], re: RegExp): number {
  if (!rows.length) return 0;
  const hit = rows.filter((r) => re.test(`${r.fuel_type ?? ""} ${r.atv_type ?? ""}`)).length;
  return hit / rows.length;
}

export interface MaintenanceDepthInput {
  fullName: string;
  makeName: string;
  vehicles: Row[];
  ctx?: MaintenanceContext;
}

export function buildMaintenanceModelDepth({
  fullName,
  makeName,
  vehicles,
  ctx = {},
}: MaintenanceDepthInput): NarrativeSection[] {
  if (vehicles.length < 2) return [];

  const profile = buildMaintenanceProfile(vehicles as never[], ctx);
  const yearRows = buildMaintenanceYearRows(vehicles as never[], ctx);
  if (!yearRows.length) return [];

  const years = yearRows.map((r) => r.year);
  const newest = years[0]!;
  const oldest = years[years.length - 1]!;
  const hybrid = share(vehicles, /hybrid/i);
  const ev = share(vehicles, /electric|bev/i);
  const diesel = share(vehicles, /diesel/i);
  const cylList = vehicles.map((v) => num(v.cylinders)).filter((n): n is number => n !== null);
  const avgCyl = cylList.length ? cylList.reduce((a, b) => a + b, 0) / cylList.length : null;
  const dispList = vehicles.map((v) => num(v.displacement)).filter((n): n is number => n !== null);
  const maxDisp = dispList.length ? Math.max(...dispList) : null;
  const minDisp = dispList.length ? Math.min(...dispList) : null;
  const cvt = vehicles.some((v) => /cvt|variable/i.test(v.transmission ?? ""));
  const manual = vehicles.some((v) => /manual/i.test(v.transmission ?? ""));
  const awd = vehicles.some((v) => /all[- ]?wheel|awd|4wd|4x4|4-wheel|four/i.test(v.drive ?? ""));
  const weight = ctx.curbWeightLbs ?? null;
  const oil = profile.oilIntervalMiles;
  const out: NarrativeSection[] = [];

  /* 1 — the number, and where it comes from on THIS model */
  const leadBits: string[] = [];
  if (profile.isElectric) {
    leadBits.push(
      `${fullName}: no oil service at any mileage. Every one of the ${n0(vehicles.length)} configurations on file for ${oldest}-${newest} is battery-electric, which deletes oil, filters, plugs, belts and exhaust work from the schedule.`,
    );
  } else {
    const delta = oil - BASE_OIL;
    leadBits.push(
      `${fullName} oil interval: ${n0(oil)} miles. Site-wide average is ${n0(BASE_OIL)}, so this model runs ${delta === 0 ? "exactly on it" : `${n0(Math.abs(delta))} miles ${delta > 0 ? "longer" : "shorter"}`}. ${profile.oilReason}`,
    );
    leadBits.push(
      `The figure is derived from ${n0(vehicles.length)} recorded configurations across ${years.length} model years (${oldest}-${newest})${
        avgCyl ? `, averaging ${avgCyl.toFixed(1)} cylinders` : ""
      }${
        maxDisp && minDisp && maxDisp !== minDisp
          ? ` and spanning ${minDisp.toFixed(1)}L to ${maxDisp.toFixed(1)}L`
          : maxDisp
            ? ` on a ${maxDisp.toFixed(1)}L engine`
            : ""
      }.`,
    );
  }
  if (hybrid > 0.15 && !profile.isElectric)
    leadBits.push(
      `${Math.round(hybrid * 100)}% of the configurations here are hybrid. Hybrids brake regeneratively, so pads and rotors on those trims routinely reach 80,000-100,000 miles instead of the 40,000-60,000 a gas ${fullName} sees.`,
    );
  if (diesel > 0.05)
    leadBits.push(
      `${Math.round(diesel * 100)}% are diesel: add a fuel-filter change every 20,000-30,000 miles and DEF top-ups every 5,000-10,000 miles, neither of which appears on the gas schedule.`,
    );
  out.push({ heading: `What the ${fullName} schedule is built from`, body: leadBits.join(" ") });

  /* 2 — the years the schedule actually moved (unique per model) */
  const changed = yearRows.filter((r) => r.changeNote);
  const changeBody = changed.length
    ? `${changed.length} model ${changed.length === 1 ? "year" : "years"} changed the ${fullName} schedule: ${changed
        .slice(0, 4)
        .map((r) => `${r.year} (${r.changeNote})`)
        .join(", ")}. Use the year selector above — servicing a ${changed[0]!.year} or newer car to the older interval is the common mistake.`
    : profile.isElectric
      ? `The ${fullName} schedule is unchanged across ${oldest}-${newest}: battery-electric in every year on file, no oil service in any of them, same ${n0(yearRows[0]!.rotationMiles)}-mile tire rotation throughout. A ${oldest} and a ${newest} get serviced identically.`
      : `The ${fullName} schedule is unchanged across ${oldest}-${newest}: same powertrain family, same ${n0(oil)}-mile oil interval, same ${n0(yearRows[0]!.rotationMiles)}-mile rotation every year on file. A ${oldest} and a ${newest} get serviced identically.`;
  out.push({ heading: `Year-to-year changes`, body: changeBody });

  /* 3 — transmission and drivetrain fluid, model-specific */
  const transBits: string[] = [];
  if (profile.isElectric) {
    transBits.push(
      `There is no transmission fluid service on the ${fullName}: a single-speed reduction gearbox replaces the multi-speed automatic, and its oil is a roughly 100,000-mile item or never, depending on trim.`,
    );
    if (awd)
      transBits.push(
        `Dual-motor ${fullName}s drive each axle through their own reduction gear, so there is no transfer case and no propshaft differential to service — only that gear oil, doubled.`,
      );
    transBits.push(
      `Brake fluid every 2-3 years is the item owners actually miss. Regeneration means the friction brakes barely work, so the fluid ages by moisture, not heat, and nothing on the dash tells you.`,
    );
  } else {
    if (cvt)
      transBits.push(
        `${fullName} CVT fluid is the expensive omission. Budget every 30,000-60,000 miles at $180-$400; a neglected CVT is a $4,000-$8,000 replacement, not a repair.`,
      );
    else if (manual)
      transBits.push(
        `Manual gearbox oil: 30,000-60,000 miles, $120-$220. Cheaper and more forgiving than the automatics in this class, but skipping it is what makes second gear crunch.`,
      );
    else
      transBits.push(
        `Automatic transmission fluid: 60,000-100,000 miles, $200-$450 at an independent shop. "Lifetime fluid" claims mean the fluid lasts the warranty, not the car.`,
      );
    if (awd)
      transBits.push(
        `AWD/4WD configurations exist for this model, so add transfer-case and differential fluid at 30,000-60,000 miles, $90-$200 per unit. Front-drive ${fullName}s skip that line entirely.`,
      );
  }
  out.push({
    heading: profile.isElectric ? `Driveline and brake fluid` : `Transmission and driveline fluid`,
    body: transBits.join(" "),
  });


  /* 4 — cost math from this model's own hardware */
  const oilCost = profile.isElectric
    ? 0
    : avgCyl && avgCyl >= 7.5
      ? 110
      : avgCyl && avgCyl >= 5.5
        ? 90
        : 70;
  const perYear = profile.isElectric
    ? 260
    : Math.round(((12000 / oil) * oilCost + 140) / 10) * 10;
  const costBits = [
    profile.isElectric
      ? `Budget about $${perYear} a year at 12,000 miles: tire rotation, cabin filter, brake fluid, wiper blades. No oil, no plugs, no timing components.`
      : `Budget about $${perYear} a year at 12,000 miles: ${(12000 / oil).toFixed(1)} oil services at roughly $${oilCost} each${avgCyl && avgCyl >= 7.5 ? " (7-8 quarts, not 4-5)" : ""}, plus rotation, filters and inspection.`,
    `Shop labour runs $${LABOUR_LOW}-$${LABOUR_HIGH} an hour independent, and dealer rates run 30-60% above that${
      /mercedes|bmw|audi|porsche|land rover|jaguar|lexus|genesis|volvo|maserati|bentley/i.test(makeName)
        ? ` — on a ${makeName} that gap is the single biggest controllable cost in the schedule`
        : ""
    }.`,
  ];
  if (weight && weight > 4500)
    costBits.push(
      `At ${n0(weight)} lbs this is a heavy vehicle, and tires and brakes price accordingly: expect $900-$1,600 a set and pad-and-rotor jobs at the top of the range.`,
    );
  out.push({ heading: `What a year of ${fullName} maintenance costs`, body: costBits.join(" ") });

  /* 5 — severe service, phrased against this model's likely duty cycle */
  const towing = ctx.towingLbs ?? null;
  out.push({
    heading: `When to halve these intervals`,
    body: profile.isElectric
      ? `Severe duty on an EV is not what it is on a gas car — there is no oil, transmission fluid or differential service to shorten. What actually suffers is tires and the 12V battery: instant torque plus ${n0(weight ?? 4000)} lbs of curb weight wears tread fast, so rotate at ${n0(yearRows[0]!.rotationMiles)} miles without stretching it, and load-test the 12V every year after year three. Cold climates also age brake fluid and seize calipers faster, because regeneration means the friction brakes are barely used. The owner's manual is the authority for warranty purposes; these figures are what the hardware actually wants.`
      : `${
          towing && towing >= 5000
            ? `Rated to ${n0(towing)} lbs of towing. Tow anywhere near that and the severe-duty column applies: oil at ${n0(Math.round(oil / 2 / 500) * 500)} miles, transmission fluid at half the figure above, and brake fluid annually.`
            : `Severe duty is not off-roading — it is short trips under 10 minutes, sub-freezing starts, stop-and-go traffic and roof-rack loads. That is most commuting.`
        } Cut oil, transmission and differential intervals in half if two or more apply. The owner's manual is the authority for warranty purposes; these figures are what the hardware actually wants.`,
  });


  return out;
}
